// Enhanced Video Player with frame capture, timeline markers, and gallery
import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { VideoFormatInfo } from "@/components/VideoFormatInfo"
import { VideoTimeline } from "@/components/VideoTimeline"
import { FrameGallery } from "@/components/FrameGallery"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Rewind,
  FastForward,
  Camera,
  ChevronDown,
  ChevronUp,
  Image,
  Keyboard,
  Layers
} from "lucide-react"
import { useKeyboardShortcuts, KeyboardShortcut } from '@/hooks/use-keyboard-shortcuts'
import { useTouchGestures } from '@/hooks/use-touch-gestures'
import { KeyboardShortcutsHelp } from '@/components/KeyboardShortcutsHelp'
import { JumpToFramePopover } from '@/components/JumpToFramePopover'
import { PlaybackSpeedSelector } from '@/components/PlaybackSpeedSelector'
import { FrameExportOptions } from '@/components/FrameExportOptions'
import { BatchExtractionModal } from '@/components/BatchExtractionModal'
import { ExportFormat, exportCanvasAsImage } from '@/lib/frame-export-utils'
import { CaptureFlash } from '@/components/CaptureFlash'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { 
  formatFrameNumber, 
  formatFrameFilename,
  getVideoFrameRate, 
  VideoFormat,
  extractFrameToCanvas
} from '@/lib/utils'
import { useVideoWithAudit } from '@/features/videos/use-video-audit'
import { useFrames } from '@/features/frames/use-frames'
import { captureAndStoreFrame, CapturedFrame } from '@/features/frames/frames-store'
import { track } from '@/lib/track'
import { toast } from 'sonner'
import { LOCAL_LIBRARY_ID } from '@/lib/browser-persistence'

/**
 * Validate that a URL is safe to use as a video source.
 * This function prevents XSS attacks by only allowing:
 * - blob: URLs (created from user file uploads, local origin only)
 * - data:video/ URLs (inline video data)
 * - https: URLs without embedded credentials
 * 
 * Dangerous protocols (javascript:, vbscript:, etc.) are explicitly rejected.
 * 
 * @param url The URL to validate
 * @returns true if the URL is safe to use as a video source
 */
function isValidVideoUrl(url: string | null): url is string {
  if (!url) return false
  
  // Explicitly reject dangerous protocols first (defense in depth)
  const dangerousProtocols = ['javascript:', 'vbscript:', 'data:text/', 'data:application/']
  const lowerUrl = url.toLowerCase()
  if (dangerousProtocols.some(proto => lowerUrl.startsWith(proto))) {
    return false
  }
  
  // Only allow blob: URLs from the current origin
  if (url.startsWith('blob:')) {
    try {
      const blobUrl = new URL(url)
      // Verify it's a valid blob protocol and from same origin
      // Blob URLs are safe as they are created locally via URL.createObjectURL()
      return blobUrl.protocol === 'blob:' && 
             (blobUrl.origin === window.location.origin || blobUrl.origin === 'null')
    } catch {
      return false
    }
  }
  
  // Allow data URLs for video content only (not text/html or other dangerous types)
  if (url.startsWith('data:video/')) {
    // Verify it's a valid video data URL format - strict pattern matching
    return /^data:video\/[a-z0-9+.-]+;base64,[A-Za-z0-9+/=]+$/i.test(url) || 
           /^data:video\/[a-z0-9+.-]+,[A-Za-z0-9%+/=]+$/i.test(url)
  }
  
  // Allow HTTPS media URLs without embedded credentials.
  if (url.startsWith('https://')) {
    try {
      const parsedUrl = new URL(url)
      // Only allow HTTPS protocol (not HTTP)
      if (parsedUrl.protocol !== 'https:') {
        return false
      }
      // Block URLs with credentials embedded (security risk)
      if (parsedUrl.username || parsedUrl.password) {
        return false
      }
      return true
    } catch {
      return false
    }
  }

  return false
}

interface EnhancedVideoPlayerProps {
  videoSrc: string | null
  videoFormat: VideoFormat | null
  videoFileSize: number | null
  onFrameChange: (frameNumber: number) => void
  videoRef?: React.RefObject<HTMLVideoElement | null>
  canvasRef?: React.RefObject<HTMLCanvasElement | null>
  videoName: string | null
  videoId?: string | null
  initialFrame?: number // For jumping to a specific frame on load
}

export function EnhancedVideoPlayer({ 
  videoSrc, 
  videoFormat, 
  videoFileSize, 
  onFrameChange,
  videoRef: externalVideoRef,
  canvasRef: externalCanvasRef,
  videoName,
  videoId,
  initialFrame
}: EnhancedVideoPlayerProps) {
  const internalVideoRef = useRef<HTMLVideoElement>(null)
  const videoIdRef = useRef<string | null>(null)
  const internalCanvasRef = useRef<HTMLCanvasElement>(null)
  const videoContainerRef = useRef<HTMLDivElement>(null)
  const requestRef = useRef<number | undefined>(undefined)
  
  const videoRef = externalVideoRef || internalVideoRef
  const canvasRef = externalCanvasRef || internalCanvasRef
  
  const [isPlaying, setIsPlaying] = useState(false)
  const [isReversePlaying, setIsReversePlaying] = useState(false)
  const [currentFrame, setCurrentFrame] = useState(0)
  const [totalFrames, setTotalFrames] = useState(0)
  const [fps, setFps] = useState(30)
  const [duration, setDuration] = useState(0)
  const [resolution, setResolution] = useState<{ width: number; height: number } | null>(null)
  const [isCapturing, setIsCapturing] = useState(false)
  const [isGalleryOpen, setIsGalleryOpen] = useState(true)
  const [playbackSpeed, setPlaybackSpeed] = useState(1)
  const [showCaptureFlash, setShowCaptureFlash] = useState(false)
  const [recentCapturedFrames, setRecentCapturedFrames] = useState<number[]>([])
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false)
  const { logVideoAction } = useVideoWithAudit()
  
  // Frame management hook
  const {
    frames,
    isLoading: isLoadingFrames,
    loadFrames,
    selection,
    actions,
    filterOptions,
    updateFilter,
    clearFilters,
    capturedFrameNumbers,
    getFrameByNumber,
    highlightedFrameId,
    highlightFrame
  } = useFrames({
    userId: LOCAL_LIBRARY_ID,
    videoId: videoId || undefined,
    autoLoad: !!videoId
  })

  // Selected frame numbers for timeline highlighting
  const selectedFrameNumbers = useMemo(() => {
    return new Set(
      frames
        .filter(f => selection.selectedIds.has(f.id))
        .map(f => f.frameNumber)
    )
  }, [frames, selection.selectedIds])
  
  // Validate video source
  const safeVideoSrc = useMemo(() => {
    if (isValidVideoUrl(videoSrc)) {
      return videoSrc
    }
    if (videoSrc) {
      console.warn('Invalid video URL rejected:', String(videoSrc).substring(0, 50))
    }
    return null
  }, [videoSrc])
  
  // Update videoIdRef
  useEffect(() => {
    if (videoId) {
      videoIdRef.current = videoId
    } else {
      const urlVideoId = new URLSearchParams(window.location.search).get('video')
      if (urlVideoId) {
        videoIdRef.current = urlVideoId
      }
    }
  }, [videoId])

  // Reload frames when videoId changes
  useEffect(() => {
    if (videoId) {
      loadFrames()
    }
  }, [videoId, loadFrames])
  
  // Handle video metadata loaded
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    
    const handleMetadataLoaded = () => {
      const videoDuration = video.duration
      const videoFps = getVideoFrameRate(video) || 30
      const frames = Math.floor(videoDuration * videoFps)
      
      setDuration(videoDuration)
      setFps(videoFps)
      setTotalFrames(frames)
      setCurrentFrame(0)
      setResolution({
        width: video.videoWidth,
        height: video.videoHeight
      })
      
      video.currentTime = 0
      setTimeout(() => renderCurrentFrame(), 100)
      
      // Jump to initial frame if specified
      if (initialFrame !== undefined && initialFrame > 0) {
        const newTime = initialFrame / videoFps
        video.currentTime = Math.min(videoDuration, newTime)
        setCurrentFrame(initialFrame)
        onFrameChange(initialFrame)
      }
      
      // Update video metadata in store
      if (safeVideoSrc) {
        const vid = new URLSearchParams(window.location.search).get('video')
        if (vid) {
          import('@/lib/utils').then(({ formatDuration }) => {
            import('@/features/videos/videos-store').then(({ updateVideoMetadata }) => {
              const formattedDuration = formatDuration(videoDuration)
              updateVideoMetadata(vid, {
                duration: formattedDuration,
                frameCount: frames
              })
              
              logVideoAction('video_metadata_updated', `Video metadata updated for ${videoName || 'video'}`, {
                videoId: vid,
                videoName: videoName || 'video',
              }, { 
                toast: false,
                additionalDetails: {
                  duration: formattedDuration,
                  frameCount: frames,
                  resolution: { width: video.videoWidth, height: video.videoHeight }
                }
              })
            })
          })
        }
      }
    }
    
    video.addEventListener('loadedmetadata', handleMetadataLoaded)
    return () => video.removeEventListener('loadedmetadata', handleMetadataLoaded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeVideoSrc, videoName, logVideoAction, initialFrame])

  // Set video source safely through ref to prevent DOM text reinterpretation
  // Security: safeVideoSrc is already validated by isValidVideoUrl()
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (safeVideoSrc) {
      // Use setAttribute for security - prevents any potential HTML parsing
      video.setAttribute('src', safeVideoSrc)
      video.load()
    } else {
      video.removeAttribute('src')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeVideoSrc])
  
  // Handle seeking and time updates
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    
    const handleTimeUpdate = () => {
      if (!isPlaying && !isReversePlaying) return
      
      const newFrame = Math.floor(video.currentTime * fps)
      if (newFrame !== currentFrame) {
        setCurrentFrame(newFrame)
        onFrameChange(newFrame)
        renderCurrentFrame()
      }
    }
    
    const handleSeeked = () => {
      const newFrame = Math.floor(video.currentTime * fps)
      setCurrentFrame(newFrame)
      onFrameChange(newFrame)
      renderCurrentFrame()
    }
    
    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('seeked', handleSeeked)
    
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('seeked', handleSeeked)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fps, currentFrame, onFrameChange, isPlaying, isReversePlaying])
  
  // Handle play/pause
  useEffect(() => {
    const video = videoRef.current
    if (!video || !safeVideoSrc) return
    
    const animate = () => {
      if (!video) return
      
      if (isPlaying) {
        if (video.paused) {
          video.play()
          logVideoAction('video_played', `Video playback started: ${videoName || 'video'}`, {
            videoId: videoIdRef.current || crypto.randomUUID(),
            videoName: videoName || 'Unknown video',
          }, { toast: false, additionalDetails: { direction: 'forward' } })
        }
        if (video.currentTime >= video.duration) {
          video.currentTime = 0
        }
      } else if (isReversePlaying) {
        if (!video.paused) video.pause()
        
        const frameTime = 1 / fps
        if (video.currentTime <= 0) {
          video.currentTime = video.duration
        } else {
          video.currentTime = Math.max(0, video.currentTime - frameTime)
        }
      } else {
        if (!video.paused) video.pause()
      }
      
      renderCurrentFrame()
      requestRef.current = requestAnimationFrame(animate)
    }
    
    requestRef.current = requestAnimationFrame(animate)
    
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, isReversePlaying, safeVideoSrc, fps, videoName, logVideoAction])
  
  // Render current frame to canvas
  const renderCurrentFrame = useCallback(() => {
    const video = videoRef.current
    const canvas = canvasRef.current
    
    if (!video || !canvas) return
    
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
    }
    
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
  }, [videoRef, canvasRef])
  
  // Playback controls
  const handlePlay = useCallback(() => {
    setIsPlaying(true)
    setIsReversePlaying(false)
  }, [])
  
  const handlePause = useCallback(() => {
    setIsPlaying(false)
    setIsReversePlaying(false)
  }, [])
  
  const handleReversePlay = useCallback(() => {
    setIsPlaying(false)
    setIsReversePlaying(true)
  }, [])
  
  const handleStepForward = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    
    setIsPlaying(false)
    setIsReversePlaying(false)
    
    const frameTime = 1 / fps
    video.currentTime = Math.min(video.duration, video.currentTime + frameTime)
    setTimeout(() => renderCurrentFrame(), 0)
  }, [fps, renderCurrentFrame, videoRef])
  
  const handleStepBackward = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    
    setIsPlaying(false)
    setIsReversePlaying(false)
    
    const frameTime = 1 / fps
    video.currentTime = Math.max(0, video.currentTime - frameTime)
    setTimeout(() => renderCurrentFrame(), 0)
  }, [fps, renderCurrentFrame, videoRef])
  
  // Skip forward/backward by multiple frames
  const handleSkipForward = useCallback((frameCount: number = 10) => {
    const video = videoRef.current
    if (!video) return
    
    setIsPlaying(false)
    setIsReversePlaying(false)
    
    const frameTime = frameCount / fps
    video.currentTime = Math.min(video.duration, video.currentTime + frameTime)
    setTimeout(() => renderCurrentFrame(), 0)
  }, [fps, renderCurrentFrame, videoRef])
  
  const handleSkipBackward = useCallback((frameCount: number = 10) => {
    const video = videoRef.current
    if (!video) return
    
    setIsPlaying(false)
    setIsReversePlaying(false)
    
    const frameTime = frameCount / fps
    video.currentTime = Math.max(0, video.currentTime - frameTime)
    setTimeout(() => renderCurrentFrame(), 0)
  }, [fps, renderCurrentFrame, videoRef])
  
  // Frame change handler - must be defined before functions that depend on it
  const handleFrameChange = useCallback((frame: number) => {
    const video = videoRef.current
    if (!video) return
    
    setIsPlaying(false)
    setIsReversePlaying(false)
    
    const newTime = frame / fps
    video.currentTime = Math.min(duration, newTime)
    setCurrentFrame(frame)
    onFrameChange(frame)
    setTimeout(() => renderCurrentFrame(), 0)
  }, [duration, fps, onFrameChange, renderCurrentFrame, videoRef])
  
  // Jump to first/last frame
  const handleGoToFirstFrame = useCallback(() => {
    handleFrameChange(0)
  }, [handleFrameChange])
  
  const handleGoToLastFrame = useCallback(() => {
    if (totalFrames > 0) {
      handleFrameChange(totalFrames - 1)
    }
  }, [totalFrames, handleFrameChange])
  
  // Jump to specific time
  const handleJumpToTime = useCallback((time: number) => {
    const video = videoRef.current
    if (!video) return
    
    setIsPlaying(false)
    setIsReversePlaying(false)
    
    video.currentTime = Math.min(duration, Math.max(0, time))
    const newFrame = Math.floor(time * fps)
    setCurrentFrame(newFrame)
    onFrameChange(newFrame)
    setTimeout(() => renderCurrentFrame(), 0)
  }, [duration, fps, onFrameChange, renderCurrentFrame, videoRef])
  
  // Playback speed control
  const handleSpeedChange = useCallback((speed: number) => {
    const video = videoRef.current
    if (video) {
      video.playbackRate = speed
    }
    setPlaybackSpeed(speed)
  }, [videoRef])
  
  // Handle frame capture - store in the browser-local library
  const handleCaptureFrame = useCallback(async () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    
    if (!video || !canvas || !videoId) {
      toast.error('Unable to capture frame')
      return
    }
    
    setIsCapturing(true)
    setShowCaptureFlash(true)
    
    // Reset flash after animation
    setTimeout(() => setShowCaptureFlash(false), 200)
    
    try {
      // Extract current frame to canvas
      extractFrameToCanvas(video, canvas)
      
      // Store frame in IndexedDB
      await captureAndStoreFrame(
        canvas,
        videoId,
        videoName || 'Unknown Video',
        currentFrame + 1, // 1-indexed for display
        video.currentTime,
        LOCAL_LIBRARY_ID,
        videoFormat
      )
      
      // Track recent captured frames
      setRecentCapturedFrames(prev => {
        const updated = [currentFrame + 1, ...prev.filter(f => f !== currentFrame + 1)]
        return updated.slice(0, 10) // Keep last 10
      })
      
      // Reload frames to update UI
      await loadFrames()
      track('captured-frame')

      // Log to audit
      logVideoAction('frame_extracted', `Frame ${currentFrame + 1} captured`, {
        videoId,
        videoName: videoName || 'Unknown video',
        frameNumber: currentFrame + 1,
      }, { 
        toast: false,
        additionalDetails: {
          timestamp: video.currentTime,
          resolution: { width: canvas.width, height: canvas.height }
        }
      })
      
      toast.success(`Frame ${currentFrame + 1} captured`)
    } catch (error) {
      console.error('Failed to capture frame:', error)
      
      if (error instanceof Error && error.message.includes('already captured')) {
        toast.info(`Frame ${currentFrame + 1} was already captured`)
      } else {
        toast.error('Failed to capture frame')
      }
    } finally {
      setIsCapturing(false)
    }
  }, [videoRef, canvasRef, videoId, videoName, currentFrame, videoFormat, loadFrames, logVideoAction])
  
  // Handle direct frame download (without saving to library)
  const handleDownloadFrame = async (format: ExportFormat, quality?: number) => {
    const video = videoRef.current
    const canvas = canvasRef.current
    
    if (!video || !canvas) {
      toast.error('Unable to export frame')
      return
    }
    
    try {
      extractFrameToCanvas(video, canvas)
      const filename = formatFrameFilename(currentFrame + 1, videoName)
      await exportCanvasAsImage(canvas, filename, format, quality)
      toast.success(`Frame ${currentFrame + 1} downloaded as ${format.toUpperCase()}`)
    } catch (error) {
      console.error('Failed to download frame:', error)
      toast.error('Failed to download frame')
    }
  }
  
  // Handle clicking on a timeline marker
  const handleTimelineMarkerClick = (frameNumber: number) => {
    const frame = getFrameByNumber(frameNumber)
    if (frame) {
      highlightFrame(frame.id)
      // Also scroll gallery to show the frame
    }
    // Jump to frame in video
    handleFrameChange(frameNumber)
  }
  
  // Handle clicking on a frame in the gallery
  const handleGalleryFrameClick = (frame: CapturedFrame) => {
    // Jump video to that frame
    handleFrameChange(frame.frameNumber)
    // Highlight it
    highlightFrame(frame.id)
  }
  
  // Handle hovering on a frame in gallery
  const handleGalleryFrameHover = (frame: CapturedFrame | null) => {
    if (frame) {
      highlightFrame(frame.id)
    } else {
      highlightFrame(null)
    }
  }
  
  // Toggle gallery visibility
  const handleToggleGallery = useCallback(() => {
    setIsGalleryOpen(prev => !prev)
  }, [])

  // Handle batch extraction
  const handleBatchExtract = useCallback(async (frameNumbers: number[]) => {
    const video = videoRef.current
    const canvas = canvasRef.current
    
    if (!video || !canvas || !videoId) {
      throw new Error('Unable to extract frames')
    }
    
    // Extract each frame
    for (let i = 0; i < frameNumbers.length; i++) {
      const frameNum = frameNumbers[i]
      const frameTime = (frameNum - 1) / fps
      
      // Seek to frame
      video.currentTime = frameTime
      // Wait for seek to complete
      await new Promise(resolve => {
        const handleSeeked = () => {
          video.removeEventListener('seeked', handleSeeked)
          resolve(void 0)
        }
        video.addEventListener('seeked', handleSeeked)
      })
      
      // Extract frame
      extractFrameToCanvas(video, canvas)
      
      // Store frame
      await captureAndStoreFrame(
        canvas,
        videoId,
        videoName || 'Unknown Video',
        frameNum,
        frameTime,
        LOCAL_LIBRARY_ID,
        videoFormat
      )
    }
    
    // Reload frames to update UI
    await loadFrames()
    
    // Log to audit
    logVideoAction('batch_frame_extraction', `${frameNumbers.length} frames extracted`, {
      videoId,
      videoName: videoName || 'Unknown video',
      frameCount: frameNumbers.length,
    }, { toast: false })
    
    toast.success(`${frameNumbers.length} frames extracted successfully`)
  }, [videoRef, canvasRef, videoId, videoName, videoFormat, fps, loadFrames, logVideoAction])

  // Check if current frame is already captured
  const isCurrentFrameCaptured = capturedFrameNumbers.has(currentFrame + 1)
  
  // Keyboard shortcuts
  const keyboardShortcuts: KeyboardShortcut[] = useMemo(() => [
    // Playback
    {
      key: ' ',
      action: () => (isPlaying ? handlePause() : handlePlay()),
      description: 'Play / Pause',
      category: 'playback',
      enabled: !!safeVideoSrc,
    },
    {
      key: 'k',
      action: handlePause,
      description: 'Pause',
      category: 'playback',
      enabled: !!safeVideoSrc,
    },
    {
      key: 'j',
      action: handleReversePlay,
      description: 'Play in reverse',
      category: 'playback',
      enabled: !!safeVideoSrc,
    },
    {
      key: 'l',
      action: handlePlay,
      description: 'Play forward',
      category: 'playback',
      enabled: !!safeVideoSrc,
    },
    // Navigation
    {
      key: 'ArrowLeft',
      action: handleStepBackward,
      description: 'Previous frame',
      category: 'navigation',
      enabled: !!safeVideoSrc,
    },
    {
      key: 'ArrowRight',
      action: handleStepForward,
      description: 'Next frame',
      category: 'navigation',
      enabled: !!safeVideoSrc,
    },
    {
      key: 'ArrowLeft',
      modifiers: { shift: true },
      action: () => handleSkipBackward(10),
      description: 'Skip back 10 frames',
      category: 'navigation',
      enabled: !!safeVideoSrc,
    },
    {
      key: 'ArrowRight',
      modifiers: { shift: true },
      action: () => handleSkipForward(10),
      description: 'Skip forward 10 frames',
      category: 'navigation',
      enabled: !!safeVideoSrc,
    },
    {
      key: 'Home',
      action: handleGoToFirstFrame,
      description: 'Go to first frame',
      category: 'navigation',
      enabled: !!safeVideoSrc,
    },
    {
      key: 'End',
      action: handleGoToLastFrame,
      description: 'Go to last frame',
      category: 'navigation',
      enabled: !!safeVideoSrc,
    },
    // Capture
    {
      key: 'c',
      action: handleCaptureFrame,
      description: 'Capture current frame',
      category: 'capture',
      enabled: !!safeVideoSrc && !!videoId,
    },
    // Interface
    {
      key: 'g',
      action: handleToggleGallery,
      description: 'Toggle captured frames gallery',
      category: 'interface',
      enabled: true,
    },
  ], [
    isPlaying, safeVideoSrc, videoId,
    handlePlay, handlePause, handleReversePlay,
    handleStepForward, handleStepBackward,
    handleSkipForward, handleSkipBackward,
    handleGoToFirstFrame, handleGoToLastFrame,
    handleCaptureFrame, handleToggleGallery
  ])
  
  const { isHelpOpen, setIsHelpOpen } = useKeyboardShortcuts({
    shortcuts: keyboardShortcuts,
    enabled: !!safeVideoSrc,
  })
  
  // Touch gestures for mobile frame navigation
  useTouchGestures(videoContainerRef, {
    onSwipeLeft: handleStepForward,
    onSwipeRight: handleStepBackward,
    enabled: !!safeVideoSrc,
    threshold: 30, // Lower threshold for easier swiping
  })
  
  return (
    <div className="flex flex-col w-full gap-4">
      {/* Video format information */}
      {videoFormat && (
        <VideoFormatInfo 
          format={videoFormat}
          fileSize={videoFileSize}
          resolution={resolution}
          duration={duration}
        />
      )}
    
      {/* Video canvas */}
      <Card ref={videoContainerRef} className="relative w-full overflow-hidden aspect-video bg-muted touch-pan-y">
        {safeVideoSrc ? (
          <>
            <canvas
              ref={canvasRef}
              className="absolute top-0 left-0 w-full h-full object-contain"
            />
            {/* Capture flash effect */}
            <CaptureFlash isCapturing={showCaptureFlash} showSuccess={true} />
            {/* 
              Security: safeVideoSrc is validated by isValidVideoUrl() which:
              1. Explicitly rejects dangerous protocols (javascript:, vbscript:, data:text/)
              2. Only allows blob: URLs from the current origin (user-uploaded files)
              3. Only allows data:video/ URLs with strict pattern matching
              4. Only allows https: URLs without embedded credentials
              
              The video src attribute does not execute scripts - it only loads media.
            */}
            <video
              ref={videoRef}
              className="hidden"
              crossOrigin="anonymous"
              playsInline
            />
          </>
        ) : (
          <div className="flex items-center justify-center w-full h-full text-muted-foreground">
            No video loaded
          </div>
        )}
      </Card>
      
      {/* Frame info and controls */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium flex items-center gap-2">
            Frame: {totalFrames > 0 ? formatFrameNumber(currentFrame + 1) : "0"}
            {isCurrentFrameCaptured && (
              <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full">
                <Camera className="h-3 w-3" />
                Captured
              </span>
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            Total Frames: {totalFrames}
          </div>
        </div>
        
        {/* Timeline with markers */}
        <VideoTimeline
          currentFrame={currentFrame}
          totalFrames={totalFrames}
          capturedFrames={capturedFrameNumbers}
          selectedFrames={selectedFrameNumbers}
          highlightedFrame={
            highlightedFrameId 
              ? frames.find(f => f.id === highlightedFrameId)?.frameNumber 
              : undefined
          }
          disabled={!safeVideoSrc}
          onFrameChange={handleFrameChange}
          onMarkerClick={handleTimelineMarkerClick}
        />
        
        {/* Playback controls */}
        <TooltipProvider>
          <div className="flex flex-col gap-3 mt-2">
            {/* Main controls row */}
            <div className="flex items-center justify-center gap-2">
              {/* Skip backward */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleSkipBackward(10)}
                    disabled={!safeVideoSrc}
                  >
                    <FastForward className="h-4 w-4 rotate-180" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Skip back 10 frames (Shift+←)</TooltipContent>
              </Tooltip>
              
              {/* Reverse play */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleReversePlay}
                    disabled={!safeVideoSrc}
                  >
                    <Rewind className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Play reverse (J)</TooltipContent>
              </Tooltip>
              
              {/* Step backward */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleStepBackward}
                    disabled={!safeVideoSrc}
                  >
                    <SkipBack className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Previous frame (←)</TooltipContent>
              </Tooltip>
              
              {/* Play/Pause */}
              <Tooltip>
                <TooltipTrigger asChild>
                  {isPlaying ? (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handlePause}
                      disabled={!safeVideoSrc}
                    >
                      <Pause className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handlePlay}
                      disabled={!safeVideoSrc}
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                  )}
                </TooltipTrigger>
                <TooltipContent>{isPlaying ? 'Pause (K)' : 'Play (Space/L)'}</TooltipContent>
              </Tooltip>
              
              {/* Step forward */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleStepForward}
                    disabled={!safeVideoSrc}
                  >
                    <SkipForward className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Next frame (→)</TooltipContent>
              </Tooltip>
              
              {/* Skip forward */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleSkipForward(10)}
                    disabled={!safeVideoSrc}
                  >
                    <FastForward className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Skip forward 10 frames (Shift+→)</TooltipContent>
              </Tooltip>
              
              {/* Capture frame */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={isCurrentFrameCaptured ? "secondary" : "default"}
                    size="icon"
                    onClick={handleCaptureFrame}
                    disabled={!safeVideoSrc || !videoId || isCapturing}
                    className="transition-all hover:scale-105 active:scale-95"
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isCurrentFrameCaptured ? "Frame already captured" : "Capture frame (C)"}
                </TooltipContent>
              </Tooltip>
              
              {/* Batch extraction */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setIsBatchModalOpen(true)}
                    disabled={!safeVideoSrc || !videoId}
                    className="hidden sm:flex"
                  >
                    <Layers className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Batch extract frames</TooltipContent>
              </Tooltip>
            </div>
            
            {/* Secondary controls row */}
            <div className="flex items-center justify-between gap-2">
              {/* Left: Jump to frame, playback speed */}
              <div className="flex items-center gap-2">
                <JumpToFramePopover
                  currentFrame={currentFrame}
                  totalFrames={totalFrames}
                  fps={fps}
                  duration={duration}
                  recentFrames={recentCapturedFrames}
                  disabled={!safeVideoSrc}
                  onJumpToFrame={handleFrameChange}
                  onJumpToTime={handleJumpToTime}
                />
                
                <PlaybackSpeedSelector
                  speed={playbackSpeed}
                  onSpeedChange={handleSpeedChange}
                  disabled={!safeVideoSrc}
                />
              </div>
              
              {/* Right: Export options, keyboard shortcuts help */}
              <div className="flex items-center gap-2">
                <FrameExportOptions
                  onExport={handleDownloadFrame}
                  disabled={!safeVideoSrc}
                  frameNumber={currentFrame + 1}
                  videoName={videoName}
                />
                
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsHelpOpen(true)}
                      className="gap-2"
                    >
                      <Keyboard className="h-4 w-4" />
                      <span className="hidden sm:inline text-xs">?</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Keyboard shortcuts (?)</TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
        </TooltipProvider>
        
        {/* Keyboard shortcuts help dialog */}
        <KeyboardShortcutsHelp
          open={isHelpOpen}
          onOpenChange={setIsHelpOpen}
          shortcuts={keyboardShortcuts}
        />
      </div>
      
      {/* Frame gallery (collapsible) */}
      {videoId && (
        <Collapsible open={isGalleryOpen} onOpenChange={setIsGalleryOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between">
              <span className="flex items-center gap-2">
                <Image className="h-4 w-4" />
                Captured Frames ({frames.length})
              </span>
              {isGalleryOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            <FrameGallery
              frames={frames}
              selectedIds={selection.selectedIds}
              highlightedFrameId={highlightedFrameId}
              isLoading={isLoadingFrames}
              onToggleSelection={selection.toggleSelection}
              onSelectAll={selection.selectAll}
              onSelectNone={selection.selectNone}
              onDeleteSelected={actions.deleteSelected}
              onDeleteFrame={actions.deleteFrame}
              onDownloadSelected={actions.downloadSelected}
              onDownloadFrame={actions.downloadFrame}
              onFrameClick={handleGalleryFrameClick}
              onFrameHover={handleGalleryFrameHover}
              filterOptions={filterOptions}
              onFilterChange={updateFilter}
              onClearFilters={clearFilters}
            />
          </CollapsibleContent>
        </Collapsible>
      )}
      
      {/* Batch extraction modal */}
      <BatchExtractionModal
        open={isBatchModalOpen}
        onOpenChange={setIsBatchModalOpen}
        videoDuration={duration}
        videoFps={fps}
        videoName={videoName || 'Video'}
        totalFrames={totalFrames}
        onExtract={handleBatchExtract}
      />
    </div>
  )
}
